import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { AppliedRegistrationDiscount } from '../AppliedRegistrationDiscount.js';
import { compileToInMemoryFilter } from '../filters/InMemoryFilter.js';
import { registrationInMemoryFilterCompilers } from '../filters/inMemoryFilterDefinitions.js';
import { GenericBalance } from '../GenericBalance.js';
import { Group } from '../Group.js';
import { GroupPrice } from '../GroupSettings.js';
import { StockReservation } from '../StockReservation.js';
import { RegisterItemOption } from './checkout/RegisterItem.js';
import { RecordAnswerDecoder } from './records/RecordAnswer.js';
export class Registration extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.options = [];
        this.recordAnswers = new Map();
        this.payingOrganizationId = null;
        this.memberId = '';
        /**
         * @deprecated
         */
        this.cycle = 0;
        /// Set registeredAt to null if the member is on the waiting list for now
        this.registeredAt = null;
        this.startDate = null;
        this.endDate = null;
        this.trialUntil = null;
        /// Keep spot for this member temporarily
        this.reservedUntil = null;
        /**
         * Currently not yet used
         */
        this.deactivatedAt = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
        /**
         * @deprecated - replaced by group type
         */
        this.waitingList = false;
        this.canRegister = false;
        /**
         * @deprecated
         * Use balances instead
         */
        this.price = 0;
        /**
         * @deprecated
         * Use balances instead
         */
        this.pricePaid = 0;
        this.stockReservations = [];
        /**
         * The total balance that has been charged for this registration.
         * This includes discounts, and manually adjusted balances.
         */
        this.balances = [];
        /**
         * Discounts that were applied to this registration.
         * Note that these discounts are saved in separate balance items and
         * are not included in the price.
         *
         * Reason is that discounts can change after you've been registered
         */
        this.discounts = new Map();
    }
    /**
     * The group price inside a registration is cached and is a snapshot of the group price at the time of registration.
     * This method returns the most up to date group price we can still find.
     */
    get updatedGroupPrice() {
        var _a;
        return (_a = this.group.settings.prices.find(price => price.id === this.groupPrice.id)) !== null && _a !== void 0 ? _a : this.groupPrice;
    }
    get groupId() {
        return this.group.id;
    }
    /**
     * @danger this could be glitchy
     * The total price that has been charged for this registration.
     * This includes discounts, and manually adjusted balances.
     *
     * Note: only returns balances that are due now. For trials this can be 0 until the trial becomes payable (± 1 week before the trial ends).
     */
    get calculatedPrice() {
        return this.balances.reduce((sum, r) => sum + (r.amountOpen + r.amountPaid + r.amountPending), 0);
    }
    /**
     * @danger
     * The registration's balances only include the balance items that are due. It is possible in some weird situations that a registration
     * has discounts that are only due in more than a week, in that case, the balance could be:
     * € 0
     * € 40 is due in 2 weeks for the normal price
     * € -10 is due in 2 week for the discount
     * The discounts object will already contain the -10.
     * So this method will return 10 euro instead of €40
     */
    get calculatedPriceWithoutDiscounts() {
        // Discounts have been substracted from the calculated price already, so we have to add them back to get the price without discounts
        return this.calculatedPrice + Array.from(this.discounts.values()).reduce((sum, r) => sum + r.amount, 0);
    }
    get isTrial() {
        return this.trialUntil !== null && (this.deactivatedAt ? (this.trialUntil >= this.deactivatedAt) : (this.trialUntil > new Date()));
    }
    get description() {
        const descriptions = [];
        if (this.group.settings.getFilteredPrices().length > 1) {
            descriptions.push(this.groupPrice.name.toString());
        }
        for (const option of this.options) {
            descriptions.push(option.optionMenu.name + ': ' + option.option.name + (option.option.allowAmount ? ` x ${option.amount}` : ''));
        }
        for (const answer of this.recordAnswers.values()) {
            descriptions.push(answer.descriptionValue);
        }
        return descriptions.filter(d => !!d).join('\n');
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, registrationInMemoryFilterCompilers);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    isRecordEnabled(_) {
        return true;
    }
    getRecordAnswers() {
        return this.recordAnswers;
    }
    patchRecordAnswers(patch) {
        return this.patch({
            recordAnswers: patch,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Registration.prototype, "id", void 0);
__decorate([
    field({ decoder: Group, version: 266 })
], Registration.prototype, "group", void 0);
__decorate([
    field({ decoder: GroupPrice, version: 305 })
], Registration.prototype, "groupPrice", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RegisterItemOption), version: 305 })
], Registration.prototype, "options", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, RecordAnswerDecoder), version: 338 })
], Registration.prototype, "recordAnswers", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 250 })
], Registration.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 351 })
], Registration.prototype, "payingOrganizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 300 })
], Registration.prototype, "memberId", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], Registration.prototype, "cycle", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Registration.prototype, "registeredAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 354 })
], Registration.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 391 })
], Registration.prototype, "endDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 354 })
], Registration.prototype, "trialUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Registration.prototype, "reservedUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Registration.prototype, "deactivatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Registration.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Registration.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 16, optional: true })
], Registration.prototype, "waitingList", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 20 })
], Registration.prototype, "canRegister", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], Registration.prototype, "price", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], Registration.prototype, "pricePaid", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StockReservation), nullable: true, version: 299 })
], Registration.prototype, "stockReservations", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GenericBalance), version: 354 })
], Registration.prototype, "balances", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, AppliedRegistrationDiscount), version: 374 })
], Registration.prototype, "discounts", void 0);
//# sourceMappingURL=Registration.js.map